import { useCallback, useEffect, useRef, useState } from "react"

/**
 * ONE searched + paginated list, for the three columns of the reseller Network
 * tab and the reseller Files list.
 *
 * WHY A HOOK AND NOT THREE COPIES OF THE SAME useState BLOCK. The columns sit
 * side by side and must be INDEPENDENT — searching Customers must not reset the
 * Dealers pager — so each needs its own `{ rows, total, page, search, loading }`.
 * Three hand-rolled copies is three places to forget the same two rules:
 *
 *   1. A NEW SEARCH RESETS TO PAGE 1. Without it, typing a term while on page 4
 *      asks for page 4 of a result set that now has one page and renders an
 *      empty column over a pager that says "Page 4 of 1".
 *   2. A LATE RESPONSE MUST NOT WIN. Two keystrokes are two requests, and the
 *      first can land second. The request counter below drops any response that
 *      is not from the newest request, so the list always shows what was last
 *      asked for.
 *
 * The fetcher is passed in rather than the endpoint, so the same hook serves
 * customers, prospects, dealers and files without knowing anything about them.
 */
export interface PagedResult<T> {
  rows: T[]
  totalCount: number
  totalPages: number
}

export type PagedFetcher<T> = (params: {
  page: number
  perPage: number
  search: string
}) => Promise<PagedResult<T>>

export interface UseResellerPagedList<T> {
  rows: T[]
  totalCount: number
  totalPages: number
  page: number
  perPage: number
  search: string
  loading: boolean
  error: string | null
  setPage: (page: number) => void
  setPerPage: (perPage: number) => void
  setSearch: (search: string) => void
  /** Re-fetch the CURRENT page — after an upload, a delete, an unlink. */
  refresh: () => void
}

export function useResellerPagedList<T>(
  fetcher: PagedFetcher<T>,
  options: { perPage?: number; enabled?: boolean } = {},
): UseResellerPagedList<T> {
  const { perPage: initialPerPage = 10, enabled = true } = options

  const [rows, setRows] = useState<T[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPageState] = useState(1)
  const [perPage, setPerPageState] = useState(initialPerPage)
  const [search, setSearchState] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  // Monotonic request id. Compared on arrival so a slow earlier request cannot
  // overwrite the result of a later one (rule 2 above).
  const requestId = useRef(0)
  // The fetcher is usually an inline arrow, so it is a new function identity on
  // every render. Held in a ref rather than listed as an effect dependency, or
  // the effect would re-run on every render and never settle.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    if (!enabled) return

    const id = ++requestId.current
    let cancelled = false

    setLoading(true)
    setError(null)

    fetcherRef
      .current({ page, perPage, search })
      .then((result) => {
        if (cancelled || id !== requestId.current) return
        setRows(result.rows)
        setTotalCount(result.totalCount)
        setTotalPages(result.totalPages)
      })
      .catch(() => {
        if (cancelled || id !== requestId.current) return
        setRows([])
        setTotalCount(0)
        setTotalPages(1)
        setError("Couldn't load this list.")
      })
      .finally(() => {
        if (cancelled || id !== requestId.current) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [page, perPage, search, reloadToken, enabled])

  const setSearch = useCallback((value: string) => {
    setSearchState(value)
    setPageState(1) // rule 1
  }, [])

  const setPerPage = useCallback((value: number) => {
    setPerPageState(value)
    setPageState(1) // a bigger page size makes the current page number wrong too
  }, [])

  const refresh = useCallback(() => setReloadToken((n) => n + 1), [])

  return {
    rows,
    totalCount,
    totalPages,
    page,
    perPage,
    search,
    loading,
    error,
    setPage: setPageState,
    setPerPage,
    setSearch,
    refresh,
  }
}
